// @flow
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { 
  Loader2, 
  Search, 
  Calendar,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomInput } from '@/components/custom-ui/custom-input.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { GetItemCheckerData } from '../../api/ItemChecker.api';
import type { IItemCheckerReport } from '../../api/ItemChecker.interface';

// Zod schema for form validation
const formSchema = z.object({
  days: z
    .number()
    .min(1, 'الحد الأدنى مطلوب')
    .positive('الرجاء ادخال رقم موجب'),
});

type FormData = z.infer<typeof formSchema>;

type Props = {
  setAllData: (
    value: React.SetStateAction<IItemCheckerReport[] | null>,
  ) => void;
  setLoading: (value: React.SetStateAction<boolean>) => void;
};

export function FormDialog({ setAllData, setLoading }: Props) {
  const getItemsMutation = useMutation({
    mutationFn: GetItemCheckerData,
    onSuccess: async (data) => {
      setAllData(data?.data);
      setLoading(false);
      toast.success('تم جلب الاصناف بنجاح', {
        position: 'bottom-center',
      });
    },

    onError: (err: AxiosError<{ message?: string }>) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'حدث خطأ ما', {
        position: 'top-right',
      });
    },
  });

  // React Hook Form setup
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      days: 7,
    },
  });

  const onSubmit = async (values: FormData) => {
    setAllData(null);
    setLoading(true);
    await getItemsMutation.mutateAsync({
      days: values.days,
    });
  };

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex items-center gap-3">
        {/* Days Input with Enhanced Design */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative flex items-center gap-2 px-3 py-2 bg-background/80 dark:bg-slate-800/50 backdrop-blur-sm rounded-lg border border-border/50 dark:border-slate-700/50">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <CustomInput
              label=""
              isInvalid={Boolean(errors.days)}
              errorMessage=""
              type="number"
              placeholder="الأيام"
              id="days"
              className="w-20 border-0 bg-transparent focus:outline-none"
              {...register('days', { valueAsNumber: true })}
              disabled={isSubmitting}
              required
            />
            <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
              يوم
            </Badge>
          </div>
          {errors.days && (
            <p className="absolute -bottom-5 right-0 text-[10px] text-red-500 dark:text-red-400">
              {errors.days.message}
            </p>
          )}
        </div>

        {/* Search Button with Enhanced Design */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            "relative overflow-hidden transition-all duration-300",
            "bg-gradient-to-r from-red-600 via-orange-600 to-amber-600",
            "hover:from-red-700 hover:via-orange-700 hover:to-amber-700",
            "dark:from-red-600 dark:via-orange-600 dark:to-amber-600",
            "dark:hover:from-red-700 dark:hover:via-orange-700 dark:hover:to-amber-700",
            "text-white shadow-lg hover:shadow-xl hover:scale-105",
            "border border-white/20",
            "group px-4 py-2",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          )}
        >
          <span className="relative z-10 flex items-center gap-2">
            {isSubmitting ? (
              <>
                <Loader2
                  className="animate-spin"
                  size={16}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
                <span className="text-sm font-medium">جاري البحث...</span>
              </>
            ) : (
              <>
                <Search className="h-4 w-4 transition-transform group-hover:scale-110" />
                <span className="text-sm font-medium">بحث وتحليل</span>
                <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </span>
          {!isSubmitting && (
            <>
              <div className="absolute -top-1 -right-1">
                <Sparkles className="h-3 w-3 text-yellow-300 animate-pulse" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
